import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import i18n from "../i18n";

const FONT_UI = "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif";
const FONT_DATA = "'DM Mono', ui-monospace, 'SF Mono', Menlo, Consolas, monospace";

const s = {
  page: { fontFamily: FONT_UI, background: "#fafaf9", minHeight: "100vh", color: "#18181b", display: "flex", flexDirection: "column", width: "100%", overflowX: "hidden" },
  header: { position: "sticky", top: 0, zIndex: 100, borderBottom: "1px solid #e7e5e4", padding: "0 48px", display: "flex", alignItems: "center", justifyContent: "space-between", height: 56, background: "#ffffff" },
  logo: { fontSize: 18, fontWeight: 700, letterSpacing: "0.02em", color: "#18181b", textTransform: "uppercase" },
  logoBeta: { fontSize: 10, background: "#f4f4f5", color: "#52525b", padding: "2px 6px", borderRadius: 6, marginLeft: 8, letterSpacing: "0.02em" },
  btnPrimary: { background: "#18181b", color: "#ffffff", border: "none", borderRadius: 8, padding: "8px 18px", fontWeight: 600, fontSize: 12, letterSpacing: "0.02em", textTransform: "uppercase", cursor: "pointer", fontFamily: "inherit" },
  btnGhost: { background: "transparent", color: "#52525b", border: "1px solid #e7e5e4", borderRadius: 8, padding: "8px 18px", fontSize: 12, cursor: "pointer", fontFamily: "inherit", letterSpacing: "0.02em", textTransform: "uppercase" },
  hero: { flex: 1, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", textAlign: "center", padding: "96px 48px 80px", maxWidth: 800, margin: "0 auto", width: "100%", boxSizing: "border-box" },
  eyebrow: { fontSize: 11, letterSpacing: "0.02em", textTransform: "uppercase", color: "#52525b", marginBottom: 24 },
  headline: { fontSize: 44, fontWeight: 700, color: "#18181b", lineHeight: 1.15, marginBottom: 20, letterSpacing: "-0.01em", margin: "0 0 20px" },
  subheadline: { fontSize: 16, color: "#52525b", lineHeight: 1.75, maxWidth: 560, margin: "0 auto 44px" },
  ctaRow: { display: "flex", gap: 12, justifyContent: "center", flexWrap: "wrap" },
  btnCtaPrimary: { background: "#18181b", color: "#ffffff", border: "none", borderRadius: 8, padding: "13px 32px", fontWeight: 600, fontSize: 13, letterSpacing: "0.02em", textTransform: "uppercase", cursor: "pointer", fontFamily: "inherit" },
  btnCtaGhost: { background: "transparent", color: "#18181b", border: "1px solid #e7e5e4", borderRadius: 8, padding: "13px 32px", fontSize: 13, cursor: "pointer", fontFamily: "inherit", letterSpacing: "0.02em", textTransform: "uppercase" },
  btnCtaDemo: { background: "transparent", color: "#1f8a4c", border: "1px solid #1f8a4c33", borderRadius: 8, padding: "13px 32px", fontSize: 13, cursor: "pointer", fontFamily: "inherit", letterSpacing: "0.02em", textTransform: "uppercase" },
  divider: { height: 1, background: "#e7e5e4", maxWidth: 1100, margin: "0 auto" },
  features: { padding: "60px 48px 80px", maxWidth: 1100, margin: "0 auto", width: "100%", boxSizing: "border-box" },
  featuresLabel: { fontSize: 11, letterSpacing: "0.02em", textTransform: "uppercase", color: "#52525b", textAlign: "center", marginBottom: 36 },
  grid3: { display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 16 },
  featureCard: { background: "#ffffff", border: "1px solid #e7e5e4", borderRadius: 10, padding: "24px 22px", boxShadow: "0 1px 2px rgba(0,0,0,.04)" },
  featureAccent: { fontSize: 10, letterSpacing: "0.02em", textTransform: "uppercase", color: "#52525b", marginBottom: 12 },
  featureTitle: { fontSize: 14, fontWeight: 700, color: "#18181b", marginBottom: 10 },
  featureBody: { fontSize: 13, color: "#52525b", lineHeight: 1.7 },
  footer: { borderTop: "1px solid #e7e5e4", padding: "20px 48px", display: "flex", alignItems: "center", justifyContent: "space-between", background: "#ffffff" },
  footerLeft: { fontSize: 11, color: "#a1a1aa" },
  footerRight: { display: "flex", gap: 20 },
  footerLink: { background: "none", border: "none", color: "#52525b", fontSize: 11, cursor: "pointer", fontFamily: "inherit", letterSpacing: "0.02em", textTransform: "uppercase", padding: 0 },
  langBtn: { background: "none", border: "1px solid #e7e5e4", borderRadius: 8, color: "#18181b", fontSize: 11, padding: "4px 10px", cursor: "pointer", fontFamily: "inherit", letterSpacing: "0.02em" },
  howSection: { padding: "80px 48px", maxWidth: 1100, margin: "0 auto", width: "100%", boxSizing: "border-box" },
  howLabel: { fontSize: 11, letterSpacing: "0.02em", textTransform: "uppercase", color: "#52525b", textAlign: "center", marginBottom: 48 },
  howStep: { textAlign: "center", flex: 1 },
  howNumber: { display: "inline-block", width: 56, height: 56, borderRadius: "50%", background: "#f4f4f5", color: "#18181b", fontSize: 24, fontWeight: 700, lineHeight: "56px", textAlign: "center", marginBottom: 16 },
  howTitle: { fontSize: 15, fontWeight: 700, color: "#18181b", marginBottom: 8 },
  howBody: { fontSize: 13, color: "#52525b", lineHeight: 1.7, maxWidth: 280, margin: "0 auto" },
  compareSection: { padding: "60px 48px 80px", maxWidth: 1100, margin: "0 auto", width: "100%", boxSizing: "border-box" },
  compareGrid: { display: "grid", gridTemplateColumns: "1fr 1fr", gap: 24 },
  compareCard: (good) => ({ background: good ? "#f0fdf4" : "#fef2f2", border: `1px solid ${good ? "#bbf7d0" : "#fecaca"}`, borderRadius: 10, padding: "32px 28px" }),
  compareLabel: { fontSize: 11, letterSpacing: "0.02em", textTransform: "uppercase", marginBottom: 16, fontWeight: 700 },
  compareItem: { fontSize: 13, color: "#3f3f46", padding: "8px 0", borderBottom: "1px solid #f4f4f5", display: "flex", alignItems: "center", gap: 10 },
  previewSection: { padding: "60px 48px 80px", maxWidth: 1100, margin: "0 auto", width: "100%", boxSizing: "border-box" },
  previewCard: { background: "#ffffff", border: "1px solid #e7e5e4", borderRadius: 10, padding: "32px 28px", boxShadow: "0 1px 2px rgba(0,0,0,.04)" },
  previewHeader: { display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20, paddingBottom: 16, borderBottom: "1px solid #e7e5e4" },
  previewRow: { display: "flex", justifyContent: "space-between", alignItems: "center", padding: "10px 0", borderBottom: "1px solid #f4f4f5", fontSize: 13 },
  badgeGreen: { display: "inline-block", background: "#f0fdf4", color: "#1f8a4c", border: "1px solid #bbf7d0", borderRadius: 6, padding: "2px 8px", fontSize: 11, letterSpacing: "0.02em", textTransform: "uppercase" },
  badgeAmber: { display: "inline-block", background: "#fef3c7", color: "#a16207", border: "1px solid #fcd34d", borderRadius: 6, padding: "2px 8px", fontSize: 11, letterSpacing: "0.02em", textTransform: "uppercase" },
};

const HEADLINES = [
  "Stop Guessing. Start Tracking.",
  "Stop Refreshing. Start Receiving.",
  "Stop Chasing. Start Leading.",
  "Stop Wondering. Start Knowing.",
  "Stop Hunting. Start Watching.",
  "Stop Waiting. Start Moving.",
  "Stop Scrolling. Start Seeing.",
  "Less Checking. More Knowing.",
  "Fewer Portals. Fewer Surprises.",
  "No More Tabs. Just Alerts.",
  "You Don't Check Bookings. They Check In.",
];

const pickHeadline = () => HEADLINES[Math.floor(Math.random() * HEADLINES.length)];

export default function LandingPage({ onLogin, onRegister, onPricing, onShowTerms, onShowPrivacy, onDemo, demoLoading }) {
  const { t } = useTranslation();
  const [headline] = useState(pickHeadline);
  const [lang, setLang] = useState(localStorage.getItem("booksmart_lang") || "en");
  const [isMobile, setIsMobile] = useState(() => window.innerWidth < 768);
  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);
  const toggleLang = () => {
    const next = lang === "en" ? "fr" : "en";
    i18n.changeLanguage(next);
    localStorage.setItem("booksmart_lang", next);
    setLang(next);
  };

  const features = [
    { icon: "📡", title: "Never log into a carrier portal again", body: "We check MAERSK, Hapag-Lloyd, ONE, and your carrier of choice — automatically, on your plan's schedule." },
    { icon: "⚡", title: "Know before your client does", body: "Email, SMS, or webhook — you choose how you hear about booking changes. Business plans include SMS and outbound webhooks." },
    { icon: "📋", title: "Prove it to your client", body: "Every change timestamped. Export a CSV in one click. Daily digest emails summarize everything across your monitored bookings." },
  ];

  return (
    <div style={s.page}>
      <link
        href="https://fonts.googleapis.com/css2?family=DM+Mono:wght@400;500&display=swap"
        rel="stylesheet"
      />

      <style>{`
        @keyframes logoEntrance {
          from { opacity: 0; transform: translateX(-40px); }
          to   { opacity: 1; transform: translateX(0); }
        }
        @keyframes fadeInUp {
          from { opacity: 0; transform: translateY(24px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        .section-headline {
          animation: fadeInUp 1.2s ease-out forwards;
          opacity: 0;
        }
      `}</style>
      <header style={s.header}>
        <div style={{ display: "flex", alignItems: "center", gap: 24 }}>
          <span style={{ ...s.logo, animation: "logoEntrance 1.5s ease-out forwards" }}>Booksmart</span>
        </div>
        <div style={{ display: "flex", gap: isMobile ? 6 : 10, alignItems: "center" }}>
          {!isMobile && <button style={s.langBtn} onClick={toggleLang}>{lang === "en" ? "FR" : "EN"}</button>}
          <button style={{ ...s.btnGhost, padding: isMobile ? "6px 10px" : s.btnGhost.padding }} onClick={onLogin}>{t('nav.login')}</button>
          <button style={{ ...s.btnPrimary, padding: isMobile ? "6px 12px" : s.btnPrimary.padding }} onClick={onRegister}>{t('nav.getStarted')}</button>
        </div>
      </header>

      <div style={{ ...s.hero, padding: isMobile ? "48px 16px 60px" : s.hero.padding }}>
        <h1 style={{ ...s.headline, fontSize: isMobile ? 26 : s.headline.fontSize }}>{headline}</h1>
        <p style={{ ...s.subheadline, fontSize: isMobile ? 14 : s.subheadline.fontSize }}>{t('landing.subheadline')}</p>
        <div style={{ textAlign: "center" }}>
          <button style={s.btnCtaPrimary} onClick={onRegister}>{t('landing.startTrial')}</button>
          <div style={{ marginTop: 16, fontSize: 12, color: "#52525b" }}>
            <button onClick={onDemo} disabled={demoLoading} style={{ background: "none", border: "none", color: "#18181b", cursor: "pointer", fontFamily: "inherit", fontSize: 12, textDecoration: "underline", padding: 0 }}>
              {demoLoading ? "Loading..." : t('login.try_demo')}
            </button>
            <span style={{ margin: "0 8px" }}>·</span>
            <button onClick={onPricing} style={{ background: "none", border: "none", color: "#52525b", cursor: "pointer", fontFamily: "inherit", fontSize: 12, textDecoration: "underline", padding: 0 }}>
              {t('landing.viewPricing')}
            </button>
          </div>
          <div style={{ marginTop: 10, fontSize: 11, color: "#a1a1aa" }}>
            Free 14-day trial · No credit card · Cancel anytime
          </div>
        </div>
      </div>

      <div style={{ ...s.divider, width: "100%", maxWidth: isMobile ? "100%" : s.divider.maxWidth }} />

      {/* ── How It Works (Option A) ─────────────────────────────────────── */}
      <div style={{ ...s.howSection, padding: isMobile ? "60px 20px" : s.howSection.padding }}>
        <div className="section-headline" style={{ ...s.howLabel, color: "#18181b", fontSize: 28, fontWeight: 700, letterSpacing: "0.04em", textTransform: "none", marginBottom: 48, animationDelay: "0.2s" }}>How It Works</div>
        <div style={{ display: "flex", gap: isMobile ? 40 : 32, flexDirection: isMobile ? "column" : "row" }}>
          {[
            { num: "1", title: "Register a Booking", body: "Enter your carrier booking reference — MAERSK, Hapag-Lloyd, ONE, or your carrier of choice. Takes under 30 seconds." },
            { num: "2", title: "We Monitor 24/7", body: "Booksmart logs into carrier portals on your behalf and checks for changes on your plan's schedule. No more logging into every carrier portal one by one." },
            { num: "3", title: "Get Alerted Instantly", body: "The moment a vessel, ETD, or status changes, you get an email alert — before your client asks where their freight is." },
          ].map(step => (
            <div key={step.num} style={s.howStep}>
              <div style={s.howNumber}>{step.num}</div>
              <div style={s.howTitle}>{step.title}</div>
              <div style={s.howBody}>{step.body}</div>
            </div>
          ))}
        </div>
      </div>

      {/* ── Before vs After (Option B) ──────────────────────────────────── */}
      <div style={{ ...s.compareSection, padding: isMobile ? "40px 20px 60px" : s.compareSection.padding }}>
        <div className="section-headline" style={{ ...s.howLabel, color: "#18181b", fontSize: 28, fontWeight: 700, letterSpacing: "0.04em", textTransform: "none", marginBottom: 48, animationDelay: "0.5s" }}>Why Booksmart?</div>
        <div style={{ ...s.compareGrid, gridTemplateColumns: isMobile ? "1fr" : s.compareGrid.gridTemplateColumns }}>
          <div style={s.compareCard(false)}>
            <div style={{ ...s.compareLabel, color: "#b91c1c" }}>✕ Without Booksmart</div>
            {["Log into multiple carrier portals", "Miss changes between daily checks", "Client emails you asking for updates", "Spreadsheets and manual tracking", "Reactive — you're always catching up"].map((item, i) => (
              <div key={i} style={s.compareItem}><span style={{ color: "#b91c1c" }}>✕</span> {item}</div>
            ))}
          </div>
          <div style={s.compareCard(true)}>
            <div style={{ ...s.compareLabel, color: "#1f8a4c" }}>✓ With Booksmart</div>
            {["One dashboard for all your carriers", "Instant alerts on every change", "You update your client before they ask", "Everything tracked automatically", "Proactive — you're always ahead"].map((item, i) => (
              <div key={i} style={s.compareItem}><span style={{ color: "#1f8a4c" }}>✓</span> {item}</div>
            ))}
          </div>
        </div>
      </div>

      {/* ── Dashboard Preview (Option C) ────────────────────────────────── */}
      <div style={{ ...s.previewSection, padding: isMobile ? "40px 16px 60px" : s.previewSection.padding }}>
        <div className="section-headline" style={{ ...s.howLabel, color: "#18181b", fontSize: 28, fontWeight: 700, letterSpacing: "0.04em", textTransform: "none", marginBottom: 48, animationDelay: "0.8s" }}>What You'll See Every Morning</div>
        <div style={s.previewCard}>
          <div style={s.previewHeader}>
            <div>
              <div style={{ fontSize: 14, fontWeight: 700, color: "#18181b" }}>📊 Active Bookings</div>
              <div style={{ fontSize: 11, color: "#52525b", marginTop: 4 }}>Last checked: just now</div>
            </div>
            <div style={{ display: "flex", gap: 8 }}>
              <span style={s.badgeGreen}>3 Active</span>
              <span style={s.badgeAmber}>1 Rolled</span>
            </div>
          </div>
          {[
            { ref: "MAEU123456789", carrier: "MAERSK", vessel: "MSC DIANA", etd: "Aug 12, 2026", status: "active" },
            { ref: "HLCU987654321", carrier: "HAPAG-LLOYD", vessel: "ARICA EXPRESS", etd: "Aug 18, 2026", status: "active" },
            { ref: "EGLV456789123", carrier: "EVERGREEN", vessel: "EVER LIVING", etd: "Aug 10, 2026", status: "active" },
            { ref: "ONEY111222333", carrier: "ONE", vessel: "ONE HAMBURG", etd: "Jul 28, 2026", status: "rolled" },
          ].map(b => (
            <div key={b.ref} style={s.previewRow}>
              <span style={{ color: "#18181b", fontWeight: 600, fontFamily: FONT_DATA }}>{b.ref}</span>
              <span style={{ color: "#52525b" }}>{b.carrier}</span>
              <span style={{ color: "#52525b" }}>{b.vessel}</span>
              <span style={{ color: "#52525b" }}>{b.etd}</span>
              <span style={b.status === "active" ? s.badgeGreen : s.badgeAmber}>{b.status}</span>
            </div>
          ))}
          <div style={{ marginTop: 16, paddingTop: 12, borderTop: "1px solid #e7e5e4", display: "flex", justifyContent: "space-between", fontSize: 12, color: "#52525b" }}>
            <span>⚡ Next check in ~45 min (Business plan)</span>
            <span>📧 Alert: vessel change detected for ONEY111222333</span>
          </div>
        </div>
      </div>

      {/* ── Features (enhanced) ─────────────────────────────────────────── */}
      <div style={s.features}>
        <div className="section-headline" style={{ ...s.featuresLabel, color: "#18181b", fontSize: 28, fontWeight: 700, letterSpacing: "0.04em", textTransform: "none", marginBottom: 48, animationDelay: "1.1s" }}>Everything You Need</div>
        <div style={{ ...s.grid3, gridTemplateColumns: isMobile ? "1fr" : s.grid3.gridTemplateColumns }}>
          {features.map(f => (
            <div key={f.title} style={s.featureCard}>
              <div style={{ fontSize: 24, marginBottom: 12 }}>{f.icon}</div>
              <div style={s.featureTitle}>{f.title}</div>
              <div style={s.featureBody}>{f.body}</div>
            </div>
          ))}
        </div>
      </div>

      <footer style={{ ...s.footer, flexDirection: isMobile ? "column" : "row", gap: isMobile ? 12 : 0, padding: isMobile ? "20px 16px" : s.footer.padding, textAlign: isMobile ? "center" : undefined }}>
        <div style={s.footerLeft}>
          {t('landing.copyright')}
          <span style={{ display: isMobile ? "block" : "inline", marginLeft: isMobile ? 0 : 12, marginTop: isMobile ? 4 : 0 }}>Built by Coda Labs Inc., Quebec, Canada.</span>
        </div>
        <div style={{ ...s.footerRight, flexDirection: isMobile ? "column" : "row", gap: isMobile ? 8 : 20, alignItems: isMobile ? "center" : undefined }}>
          <a href="mailto:support@booksmarthq.io" style={{ ...s.footerLink, textDecoration: "none" }}>support@booksmarthq.io</a>
          <button style={s.footerLink} onClick={onShowTerms}>{t('legal.terms')}</button>
          <button style={s.footerLink} onClick={onShowPrivacy}>{t('legal.privacy')}</button>
        </div>
      </footer>
    </div>
  );
}
